import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Chat as ChatIcon,
  Save as SaveIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import api from '../api';
import NPCEditDialog from './NPCEditDialog';
import ConversationEditDialog from './ConversationEditDialog';
import npcSpritesData from '../../data/npc-sprites.json';

const READ_SPEEDS = ['Default', 'Slow', 'Fast'];

const NPCManager = () => {
  const [npcs, setNpcs] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedNpc, setSelectedNpc] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [npcDialogOpen, setNpcDialogOpen] = useState(false);
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [editingNpc, setEditingNpc] = useState(null);
  const [editingConversation, setEditingConversation] = useState(null);
  const [editingSentences, setEditingSentences] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    fetchNpcs();
  }, []);

  useEffect(() => {
    if (selectedNpc) {
      fetchConversations(selectedNpc.name);
    } else {
      setConversations([]);
      setSelectedConversation(null);
    }
  }, [selectedNpc]);

  const fetchNpcs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/npcs/npcs');
      setNpcs(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching NPCs:', err);
      setError('Failed to fetch NPCs');
    } finally {
      setLoading(false);
    }
  };

  const fetchConversations = async (npcName) => {
    try {
      const response = await api.get(`/npcs/npcs/${encodeURIComponent(npcName)}/conversations`);
      console.log('API Response:', response.data);
      if (response.data.length > 0) {
        console.log('First conversation sentences:', response.data[0].sentences);
        console.log('Sentences type:', typeof response.data[0].sentences);
      }
      setConversations(response.data);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to fetch conversations');
    }
  };

  const handleNpcSelect = (npc) => {
    setSelectedNpc(npc);
    setSelectedConversation(null);
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setEditingSentences(parseJSON(conversation.sentences));
    setHasUnsavedChanges(false);
  };

  const updateSentence = (index, field, value) => {
    const newSentences = [...editingSentences];
    newSentences[index] = { ...newSentences[index], [field]: value };
    setEditingSentences(newSentences);
    setHasUnsavedChanges(true);
  };

  const moveSentence = (index, direction) => {
    const newSentences = [...editingSentences];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSentences.length) return;
    [newSentences[index], newSentences[newIndex]] = [newSentences[newIndex], newSentences[index]];
    setEditingSentences(newSentences);
    setHasUnsavedChanges(true);
  };

  const addSentence = () => {
    setEditingSentences([
      ...editingSentences,
      {
        speakerName: '',
        text: '',
        speakerSpriteName: '',
        speakerSide: 0,
        readSpeed: 'Default',
        textMobileOverride: null
      }
    ]);
    setHasUnsavedChanges(true);
  };

  const deleteSentence = (index) => {
    if (!window.confirm('Delete this sentence?')) return;
    setEditingSentences(editingSentences.filter((_, i) => i !== index));
    setHasUnsavedChanges(true);
  };

  const handleSaveSentences = async () => {
    try {
      await api.put(`/npcs/conversations/${selectedConversation.id}`, {
        ...selectedConversation,
        sentences: editingSentences
      });
      setHasUnsavedChanges(false);
      await fetchConversations(selectedNpc.name);
      // Update selected conversation
      const response = await api.get(`/npcs/conversations/${selectedConversation.id}`);
      setSelectedConversation(response.data);
      setEditingSentences(parseJSON(response.data.sentences));
    } catch (err) {
      console.error('Error saving sentences:', err);
      setError('Failed to save sentences');
    }
  };

  const handleNpcAdd = () => {
    setEditingNpc(null);
    setNpcDialogOpen(true);
  };

  const handleNpcEdit = (npc) => {
    setEditingNpc(npc);
    setNpcDialogOpen(true);
  };

  const handleNpcDelete = async (npcId) => {
    if (!window.confirm('Are you sure you want to delete this NPC?')) return;

    try {
      await api.delete(`/npcs/npcs/${npcId}`);
      await fetchNpcs();
      if (selectedNpc?.id === npcId) {
        setSelectedNpc(null);
      }
    } catch (err) {
      console.error('Error deleting NPC:', err);
      setError('Failed to delete NPC');
    }
  };

  const handleNpcSave = async (npcData) => {
    try {
      if (editingNpc) {
        await api.put(`/npcs/npcs/${editingNpc.id}`, npcData);
      } else {
        await api.post('/npcs/npcs', npcData);
      }
      await fetchNpcs();
      setNpcDialogOpen(false);
    } catch (err) {
      console.error('Error saving NPC:', err);
      throw err;
    }
  };

  const handleConversationAdd = () => {
    if (!selectedNpc) {
      setError('Please select an NPC first');
      return;
    }
    setEditingConversation(null);
    setConversationDialogOpen(true);
  };

  const handleConversationEdit = (conversation) => {
    setEditingConversation(conversation);
    setConversationDialogOpen(true);
  };

  const handleConversationDelete = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await api.delete(`/npcs/conversations/${conversationId}`);
      await fetchConversations(selectedNpc.name);
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (err) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const handleConversationSave = async (conversationData) => {
    try {
      if (editingConversation) {
        await api.put(`/npcs/conversations/${editingConversation.id}`, conversationData);
      } else {
        await api.post('/npcs/conversations', {
          ...conversationData,
          npcName: selectedNpc.name
        });
      }
      await fetchConversations(selectedNpc.name);
      setConversationDialogOpen(false);
    } catch (err) {
      console.error('Error saving conversation:', err);
      throw err;
    }
  };

  const parseJSON = (jsonString) => {
    try {
      console.log('parseJSON input:', jsonString);
      console.log('parseJSON input type:', typeof jsonString);
      
      // If it's already an array/object, return it
      if (typeof jsonString === 'object' && jsonString !== null) {
        console.log('Already object, returning:', jsonString);
        return jsonString;
      }
      // Otherwise parse the string
      const parsed = JSON.parse(jsonString);
      console.log('Parsed result:', parsed);
      return parsed;
    } catch (e) {
      console.error('parseJSON error:', e, 'input was:', jsonString);
      return [];
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">NPC & Conversation Manager</Typography>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
        {/* NPCs Panel */}
        <Paper sx={{ width: '25%', p: 2, overflow: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">NPCs</Typography>
            <IconButton size="small" color="primary" onClick={handleNpcAdd}>
              <AddIcon />
            </IconButton>
          </Stack>
          <List dense>
            {npcs.map((npc) => (
              <ListItem
                key={npc.id}
                disablePadding
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => handleNpcEdit(npc)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleNpcDelete(npc.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemButton
                  selected={selectedNpc?.id === npc.id}
                  onClick={() => handleNpcSelect(npc)}
                >
                  <ListItemText
                    primary={npc.name}
                    secondary={
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {parseJSON(npc.interactions).map((interaction, idx) => (
                          <Chip key={idx} label={interaction} size="small" />
                        ))}
                      </Stack>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Conversations Panel */}
        <Paper sx={{ width: '25%', p: 2, overflow: 'auto' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Conversations</Typography>
            <IconButton
              size="small"
              color="primary"
              onClick={handleConversationAdd}
              disabled={!selectedNpc}
            >
              <AddIcon />
            </IconButton>
          </Stack>
          {selectedNpc ? (
            <List dense>
              {conversations.map((conversation) => (
                <ListItem
                  key={conversation.id}
                  disablePadding
                  secondaryAction={
                    <Stack direction="row" spacing={0.5}>
                      <IconButton size="small" onClick={() => handleConversationEdit(conversation)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleConversationDelete(conversation.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemButton
                    selected={selectedConversation?.id === conversation.id}
                    onClick={() => handleConversationSelect(conversation)}
                  >
                    <ListItemText
                      primary={conversation.assetName}
                      secondary={`${parseJSON(conversation.sentences).length} sentences`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" align="center">
              Select an NPC to view conversations
            </Typography>
          )}
        </Paper>

        {/* Details Panel */}
        <Paper sx={{ width: '50%', p: 3, overflow: 'auto' }}>
          {selectedConversation ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">{selectedConversation.assetName}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => handleConversationEdit(selectedConversation)}
                  >
                    Edit
                  </Button>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleConversationDelete(selectedConversation.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Conversation ID</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedConversation.id}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">NPC</Typography>
                  <Typography variant="body1">{selectedConversation.npcName}</Typography>
                </Box>

                <Divider />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Sentences ({editingSentences.length})</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveSentences}
                      disabled={!hasUnsavedChanges}
                      color={hasUnsavedChanges ? 'warning' : 'primary'}
                    >
                      {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={addSentence}
                    >
                      Add Sentence
                    </Button>
                  </Stack>
                </Stack>
                {editingSentences.map((sentence, idx) => {
                  const availableSprites = selectedConversation?.npcName ? (npcSpritesData[selectedConversation.npcName] || []) : [];
                  const allSprites = Object.values(npcSpritesData).flat();
                  
                  return (
                    <Paper key={idx} sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Stack spacing={2}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2">Sentence #{idx + 1}</Typography>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton
                              size="small"
                              onClick={() => moveSentence(idx, 'up')}
                              disabled={idx === 0}
                            >
                              <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => moveSentence(idx, 'down')}
                              disabled={idx === editingSentences.length - 1}
                            >
                              <ArrowDownIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => deleteSentence(idx)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>

                        <TextField
                          label="Speaker Name"
                          value={sentence.speakerName || ''}
                          onChange={(e) => updateSentence(idx, 'speakerName', e.target.value)}
                          fullWidth
                          size="small"
                        />

                        <TextField
                          label="Text"
                          value={sentence.text || ''}
                          onChange={(e) => updateSentence(idx, 'text', e.target.value)}
                          multiline
                          rows={3}
                          fullWidth
                          size="small"
                          helperText="Use <br> for line breaks, <color=#50BAAC>text</color> for colored text"
                        />

                        <TextField
                          label="Mobile Text Override (optional)"
                          value={sentence.textMobileOverride || ''}
                          onChange={(e) => updateSentence(idx, 'textMobileOverride', e.target.value || null)}
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                        />

                        <Stack direction="row" spacing={2}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Speaker Sprite</InputLabel>
                            <Select
                              value={sentence.speakerSpriteName || ''}
                              onChange={(e) => updateSentence(idx, 'speakerSpriteName', e.target.value)}
                              label="Speaker Sprite"
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {availableSprites.length > 0 && (
                                <MenuItem disabled>
                                  <em>— {selectedConversation.npcName} Sprites —</em>
                                </MenuItem>
                              )}
                              {availableSprites.map((sprite) => (
                                <MenuItem key={sprite} value={sprite}>
                                  {sprite}
                                </MenuItem>
                              ))}
                              {availableSprites.length > 0 && allSprites.length > availableSprites.length && (
                                <MenuItem disabled>
                                  <em>— All Other Sprites —</em>
                                </MenuItem>
                              )}
                              {allSprites
                                .filter(sprite => !availableSprites.includes(sprite))
                                .map((sprite) => (
                                  <MenuItem key={sprite} value={sprite}>
                                    {sprite}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel>Speaker Side</InputLabel>
                            <Select
                              value={sentence.speakerSide}
                              onChange={(e) => updateSentence(idx, 'speakerSide', e.target.value)}
                              label="Speaker Side"
                            >
                              <MenuItem value={0}>Left (0)</MenuItem>
                              <MenuItem value={1}>Right (1)</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl fullWidth size="small">
                            <InputLabel>Read Speed</InputLabel>
                            <Select
                              value={sentence.readSpeed || 'Default'}
                              onChange={(e) => updateSentence(idx, 'readSpeed', e.target.value)}
                              label="Read Speed"
                            >
                              {READ_SPEEDS.map((speed) => (
                                <MenuItem key={speed} value={speed}>
                                  {speed}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          ) : selectedNpc ? (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">{selectedNpc.name}</Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => handleNpcEdit(selectedNpc)}
                  >
                    Edit
                  </Button>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleNpcDelete(selectedNpc.id)}
                  >
                    Delete
                  </Button>
                </Stack>
              </Stack>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">NPC ID</Typography>
                  <Typography variant="body2" fontFamily="monospace">{selectedNpc.id}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>Interactions</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {parseJSON(selectedNpc.interactions).map((interaction, idx) => (
                      <Chip key={idx} label={interaction} color="primary" />
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" mb={1}>
                    Default Conversations ({parseJSON(selectedNpc.defaultConversationIds).length})
                  </Typography>
                  <Stack spacing={0.5}>
                    {parseJSON(selectedNpc.defaultConversationIds).map((id, idx) => (
                      <Typography key={idx} variant="body2" fontFamily="monospace">
                        {id}
                      </Typography>
                    ))}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Active</Typography>
                  <Chip
                    label={selectedNpc.isActive ? 'Active' : 'Inactive'}
                    color={selectedNpc.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
              </Stack>
            </Box>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                Select an NPC or conversation to view details
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>

      <NPCEditDialog
        open={npcDialogOpen}
        npc={editingNpc}
        onClose={() => setNpcDialogOpen(false)}
        onSave={handleNpcSave}
      />

      <ConversationEditDialog
        open={conversationDialogOpen}
        conversation={editingConversation}
        npcName={selectedNpc?.name}
        allConversations={conversations}
        onClose={() => setConversationDialogOpen(false)}
        onSave={handleConversationSave}
      />
    </Box>
  );
};

export default NPCManager;

